<template>
  <div>
    <FrontPageTenantBanner :selectedTenant="{ subdomain: tenantProfile.subdomain, data: tenantProfile }" />
    <div class="container single-property" v-if="!loading && !error">

      <div v-if="!previewAuction" style="margin: 0 0 1rem;">
        <router-link to="/listings">&lt; Browse Auctions</router-link>
      </div>

      <h2>{{ auction.name || auction.address }}</h2>
      <div class="clearfix">
        <div class="below-heading">
          {{ auction.city }}, {{ auction.state }} <span class="space"></span>
          APN: {{ auction.metadata.apn | truncate(20) || '(not defined)' }} <span class="space"></span>
          Auction by: <router-link to="/">{{ tenantProfile.name }}</router-link>
        </div>
        <div class="some-icons">
          <!-- add twitter, facebook, etc. icons here later -->
        </div>
      </div>

      <WithSidebar class="property-content">

        <div class="property-content" slot="main">

          <div class="photo-carousel" v-if="photos().length > 0">
            <a target="_blank" :href="mapHref()"><div class="mapbox"><v-icon color="white">place</v-icon></div></a>
            <PfxCarousel :images="photos()" :color="$store.state.theme.brandColor || '#000'" />
          </div>

          <div class="grey-box-container property-description">
            <v-tabs grow v-model="selectedTab" class="custom-tabs-bar-style" slider-color="brand">

              <v-tab :href="'#tab1'">Property Details</v-tab>
              <v-tab :href="'#tab2'">Auction Information</v-tab>
              <v-tab :href="'#tab3'">Document Vault</v-tab>

              <v-tabs-items>

                <v-tab-item :value="'tab1'">
                  <v-card>
                    <v-card-text>
                      <h4>Highlights</h4>
                      <ul class="list-bullets">
                        <li v-if="auction.metadata.buildingSize">{{ auction.metadata.buildingSize | prettySF }} Building size</li>
                        <li v-if="auction.metadata.lotSize">{{ auction.metadata.lotSize | prettySF }} Lot size</li>
                        <li v-if="auction.yearBuilt">Built {{ auction.yearBuilt }}</li>
                        <li v-if="auction.propertyType">{{ propertyTypeLabel(auction.propertyType) }}</li>
                      </ul>
                      <div class="p">
                        <h4>Location</h4>
                        {{ auction.address }}<br/>
                        {{ auction.zip }} {{ auction.city }}, {{ auction.state }}<br/>
                        <v-icon>place</v-icon> <a target="_blank" :href="mapHref()">Open map</a>
                      </div>
                      <div class="p small">
                        <h4>Description</h4>
                        {{ auction.metadata && auction.metadata.description }}
                      </div>
                      <p class="small">
                        APN: {{ auction.metadata.apn || '(not defined)' }}
                      </p>
                      <p v-for="document in auction.metadata.documents" :key="document.url">
                        <a target="_blank" :href="document.url"><button class="btn-neutral btn-small">Download {{ document.name }}</button></a>
                      </p>
                    </v-card-text>
                  </v-card>
                </v-tab-item>

                <v-tab-item :value="'tab2'">
                  <v-card>
                    <v-card-text>
                      <div class="p">
                        <h4>Auction Process</h4>
                        <ol>
                          <li>Registration Period: review the items in the document vault and perform your due diligence before the start of the auction.</li>
                          <li>Register and receive approval to participate in the auction. You need to upload the required documents in order for the seller to approve you as a registered bidder for the property.
                            <ul class="list-bullets no-margin" v-if="auction.buyerRequirements">
                              <!-- <li v-for="req in auction.buyerRequirements" :key="req.id">{{ req.name }}</li> -->
                              <li v-for="req in auction.buyerRequirements" :key="req.id">{{ getLabel(req.docType) }}</li>
                            </ul>
                          </li>
                          <li>When the auction starts, place your bids!</li>
                          <li>Every bid placed within the last 5 minutes extends the auction.</li>
                          <li>A winner is defined when the auction period is closed and the bid has exceeded the reserve price.</li>
                        </ol>
                      </div>
                      <h4>{{ auctionTypeLabel(auction.metadata.type) }}</h4>
                      <ul class="list-bullets">
                        <li>Registration opens <u>{{ auction.publishDate | prettyDateTime(auction.metadata.timezone) }}</u></li>
                        <li>Registration closes <u>{{ auction.freezeDate | prettyDateTime(auction.metadata.timezone) }}</u></li>
                        <li>Auction opens <u>{{ auction.startDate | prettyDateTime(auction.metadata.timezone) }}</u></li>
                        <li>Auction ends <u>{{ auction.endDate | prettyDateTime(auction.metadata.timezone) }}</u></li>
                        <li>Starting price is {{ auction.startingPrice | prettyMoney }}</li>
                        <li>Minimum bid increment is {{ auction.minimumIncrement | prettyMoney }}</li>
                      </ul>
                      <!--<div><a><v-icon>date_range</v-icon> Add to calendar</a></div>--><!-- TODO add to calendar -->
                      <p v-if="auction.metadata.auctionInfo" class="list-bullets"><vue-markdown :source="auction.metadata.auctionInfo" /></p>
                      <p class="small">{{ auction.metadata.commissionInfo }}</p>
                    </v-card-text>
                  </v-card>
                </v-tab-item>

                <v-tab-item :value="'tab3'">
                  <v-card>
                    <v-card-text>

                      <h4>Public Documents</h4>
                      <div class="clearfix p document" v-for="file in filesPublic" :key="file.id">
                        <v-icon style="float: left;">content_copy</v-icon>
                        <div style="float: left; margin-left: 1rem;">
                          <a @click="initiatePublicDownload(file.id)">{{ file.description || file.desc }}</a><br/><!-- note: alternative attributes (desc, name, size) are from auction preview (create new listing) -->
                          Updated at {{ file.lastModified | prettyDateShort(auction.metadata.timezone) }}<br/>
                        </div>
                      </div>
                      <div class="p" v-if="filesPublic.length === 0">
                        <ul><li>No documents available.</li></ul>
                      </div>

                      <h4>Documents for Logged-In Users</h4>
                      <div v-if="authenticatedUser">
                        <div class="clearfix p document" v-for="file in filesLoggedIn" :key="file.id">
                          <v-icon style="float: left;">content_copy</v-icon>
                          <div style="float: left; margin-left: 1rem;">
                            <a @click="initiateDownload(file.id)">{{ file.description || file.desc }}</a><br/><!-- note: alternative attributes (desc, name, size) are from auction preview (create new listing) -->
                            Updated at {{ file.lastModified | prettyDateShort(auction.metadata.timezone) }}<br/>
                          </div>
                        </div>
                        <div class="p" v-if="filesLoggedIn.length === 0">
                          <ul><li>No documents available.</li></ul>
                        </div>
                      </div>
                      <div v-else class="locked-documents">
                        <p><v-icon x-large color="brand">lock</v-icon></p>
                        <p>You need to be logged-in to view these documents.</p>
                      </div>

                      <h4>Documents for Authorized Bidders</h4>
                      <div v-if="auction.role.role === auctionRoles.BUYER.value || auction.role.role === auctionRoles.WINNER.value || auction.role.role === auctionRoles.ABANDON.value || auction.role.role === auctionRoles.SELLING_AGENT.value || auction.role.role === auctionRoles.LISTING_AGENT.value || auction.role.role === auctionRoles.DELEGATE.value">
                        <div class="clearfix p document" v-for="file in filesAuthorized" :key="file.id">
                          <v-icon style="float: left;">content_copy</v-icon>
                          <div style="float: left; margin-left: 1rem;">
                            <a @click="initiateDownload(file.id)">{{ file.description || file.desc }}</a><br/><!-- note: alternative attributes (desc, name, size) are from auction preview (create new listing) -->
                            Updated at {{ file.lastModified | prettyDateShort(auction.metadata.timezone) }}<br/>
                          </div>
                        </div>
                        <div class="p" v-if="filesAuthorized.length === 0">
                          <ul><li>No documents available.</li></ul>
                        </div>
                      </div>
                      <div v-else class="locked-documents">
                        <p><v-icon x-large color="brand">lock</v-icon></p>
                        <p>You need to be registered and accepted to the auction to view these documents.</p>
                      </div>

                    </v-card-text>
                  </v-card>
                </v-tab-item>

              </v-tabs-items>

            </v-tabs>
          </div>


        </div>

        <div slot="sidebar">
          <AuctionRegisterWidget :initialAuction="auction" @refreshAuctionPage="refreshAuctionPage" :disableLinks="this.$props.disableLinks" />
          <AuctionBrokerInfoWidget type="medium" :initialAuction="auction" :disableLinks="this.$props.disableLinks" />
          <AuctionPoweredByWidget type="medium" :auctionId="auction.id" :disableLinks="this.$props.disableLinks" :disableInvite="this.$props.disableInvite" />
        </div>

      </WithSidebar>

      <HowDoesItWork :auctionPageStyle="true" />

    </div>
  </div>
</template>

<script>
  import FrontPageTenantBanner from './FrontPageTenantBanner.vue'
  import WithSidebar from './WithSidebar.vue'
  import AuctionRegisterWidget from './AuctionRegisterWidget.vue'
  import AuctionBrokerInfoWidget from './AuctionBrokerInfoWidget.vue'
  import AuctionPoweredByWidget from './AuctionPoweredByWidget.vue'
  import PfxCarousel from './elements/PfxCarousel.vue'
  import HowDoesItWork from './elements/HowDoesItWork.vue'
  import { propertyTypeLabel, auctionTypeLabel, auctionRoles, auctionStatuses, jumpToTenantWebsiteIfNeeded, fileDocTypeLabel } from '../utils/PfxUtils.js'
  import VueMarkdown from 'vue-markdown'

  import { mapGetters, mapMutations } from 'vuex'

  export default {
    components: {
      FrontPageTenantBanner,
      WithSidebar,
      AuctionRegisterWidget,
      AuctionBrokerInfoWidget,
      AuctionPoweredByWidget,
      VueMarkdown,
      PfxCarousel,
      HowDoesItWork
    },
    props: {
      previewAuction: {
        type: Object,
        required: false,
        default: () => undefined
      },
      disableLinks: {
          type: Boolean,
          required: false,
          default: false
      },
      disableInvite: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    data() {
      return {
        loading: true,
        error: false,
        auction: { metadata: {}, tenant: {}, role: {}, participants: {} },
        tenantProfile: {},
        selectedTab: '',
        filesPublic: [],
        filesLoggedIn: [],
        filesAuthorized: [],
        buyerRequirementOptions: [],
        fileDocTypeLabel
      }
    },
    computed: {
      ...mapGetters([
        'authenticatedUser',
        'getFilesForNewAuction'
      ]),
      editMode() {
        return this.$store.state.newAuctionStorage.edit === true
      },
      auctionRoles() {
        return auctionRoles
      },
      auctionStatuses() {
        return auctionStatuses
      },
      auctionId() {
        return this.$route.params.auctionId
      }
    },

    mounted() {
      this.refreshAuctionPage()
      // subscribe to auction events
      this.$events.on('auction', auction => {
        if ( auction.id === this.auction.id ) {
          Object.assign(this.auction, auction)
        }
      })
      // subscribe to authentication events
      this.$events.on('auth', () => {
        if ( this.authenticatedUser && this.auctionId ) {
          this.refreshAuctionPage()
        }
      })
    },
    beforeDestroy() {
      this.$events.off('auth')
      this.$events.off('auction')
    },
    created(){
          this.$PfxBackendApi.getBuyerRequirementsOptions().then(res => {
          // console.log('getBuyerRequirementsOptions res', res)
          res.data.forEach(document => {
            let item = { text: document.name, id: document.id }
            this.buyerRequirementOptions.push(item)
          })
          console.log('getBuyerRequirementsOptions Array: ', this.buyerRequirementOptions)
        }).catch(err => {
          console.error('getBuyerRequirementsOptions err', err)
        })
    },
    methods: {
      ...mapMutations([
        'setLoadingSpinner',
        'openDialog',
        'setAllowAutoBid'
      ]),
      propertyTypeLabel,
      auctionTypeLabel,

      getLabel(id){
        return fileDocTypeLabel(id, this.buyerRequirementOptions)
      },

      photos() {
        if (this.previewAuction) {
          const a = []
          this.auction.metadata.photos
            .filter(p => this.auction.metadata.photosRemoved.indexOf(p) === -1)
            .forEach(p => a.push(p))
          this.auction.metadata.photosAdded
            .filter(p => !p.removed)
            .forEach(p => a.push(p.dataUrl))
          return a
        }
        return this.auction.metadata.photos
      },

      refreshAuctionPage() {
        this.loading = true
        this.validateInvite()
        if (this.previewAuction) {
          this.auction = this.previewAuction
          if (this.editMode) {
            this.fetchFilesFromDocumentVault()
          } else {
            this.filesPublic = this.getFilesForNewAuction.filter(file => file.visibility === 'PUBLIC')
            this.filesLoggedIn = this.getFilesForNewAuction.filter(file => file.visibility === 'LOGGED_IN')
            this.filesAuthorized = this.getFilesForNewAuction.filter(file => file.visibility === 'AUTHORIZED')
            this.auction.status = auctionStatuses.OPEN.value
          }
          if (!this.auction.listingAgent) {
            this.auction.listingAgent = this.$store.state.user.cognitoSubject
          }
          this.auction.role = { role: auctionRoles.BUYER.value }
          this.auction.participants = { applicant: 0, buyer: 0, winner: 0, abandon: 0, total: 0 }
          this.tenantProfile = this.auction.tenant
          console.log('auction page in preview mode', this.auction)
          this.loading = false
          return
        }

        this.setLoadingSpinner(true)
        this.$PfxBackendApi.getAuction(this.auctionId).then(res => {
          console.log('auction res', res)
          this.auction = res.data || {}
          this.setAllowAutoBid(this.auction.metadata.allowAutobids || false)
          this.setLoadingSpinner(false)
          jumpToTenantWebsiteIfNeeded(this.auction, this.$store) // cannot do this until subdomain is known
          this.fetchFilesFromDocumentVault() // cannot initialize until subdomain is known
          this.fetchTenantProfile()
        }).catch(err => {
          console.error('getAuction err', err)
          this.$notify({ type: 'danger', message: 'Cannot fetch data about auction' })
          this.error = true
          this.setLoadingSpinner(false)
        }).then(() => this.loading = false)
      },

      validateInvite() {
        if(this.$store.state.user.role && (this.$store.state.user.role == 'ANONYMOUS')){
          this.$props.disableInvite = true
        }
      },

      fetchTenantProfile() {
        this.$PfxBackendApi.getTenantPublic(this.auction.tenant.subdomain).then(res => {
          console.log('getTenantPublic res', res)
          this.tenantProfile = res.data || {}
        }).catch(err => {
          console.error('getTenantPublic err', err)
        })
      },

      fetchFilesFromDocumentVault() {
        this.$PfxBackendApi.getAuctionFiles(this.auction.tenant.subdomain, this.auction.id, 'PUBLIC').then(res => {
          console.log('getAuctionFiles (PUBLIC) res', res)
          this.filesPublic = res.data
        }).catch(err => {
          console.error('getAuctionFiles (PUBLIC) err', err)
          this.$notify({ type: 'danger', message: 'Cannot fetch data about document vault files' })
        })
        if (!this.authenticatedUser) {
          return // cannot access LOGGED_IN or AUTHORIZED documents unless user is authenticated
        }
        this.$PfxBackendApi.getAuctionFiles(this.auction.tenant.subdomain, this.auction.id, 'LOGGED_IN').then(res => {
          console.log('getAuctionFiles (LOGGED_IN) res', res)
          this.filesLoggedIn = res.data
        }).catch(err => {
          if (err.status === 403) return
          console.error('getAuctionFiles (LOGGED_IN) err', err)
          this.$notify({ type: 'danger', message: 'Cannot fetch data about document vault files' })
        })
        this.$PfxBackendApi.getAuctionFiles(this.auction.tenant.subdomain, this.auction.id, 'AUTHORIZED').then(res => {
          console.log('getAuctionFiles (AUTHORIZED) res', res)
          this.filesAuthorized = res.data
        }).catch(err => {
          if (err.status === 403) return
          console.error('getAuctionFiles (AUTHORIZED) err', err)
          this.$notify({ type: 'danger', message: 'Cannot fetch data about document vault files' })
        })
      },

      initiatePublicDownload(fileId) {
        const anotherWindow = window.open('', '_blank')
        this.$PfxBackendApi.initiatePublicFileDownload(this.auction.tenant.subdomain, this.auctionId, fileId).then(res => {
          console.log('initiatePublicFileDownload res', res)
          anotherWindow.location.href = res.data.url
        }).catch(err => {
          anotherWindow.close()
          console.error('initiatePublicFileDownload err', err)
          this.openDialog({
            text: 'Download error ' + err.data.message,
            buttons: [{ title: 'Close' }]
          })
        })
      },

      initiateDownload(fileId) {
        const anotherWindow = window.open('', '_blank')
        this.$PfxBackendApi.initiateFileDownload(this.auction.tenant.subdomain, this.auctionId, fileId).then(res => {
          console.log('initiateFileDownload res', res)
          anotherWindow.location.href = res.data.url
        }).catch(err => {
          anotherWindow.close()
          console.error('initiateFileDownload err', err)
          this.openDialog({
            text: 'Download error ' + err.data.message,
            buttons: [{ title: 'Close' }]
          })
        })
      },

      mapHref() {
        return 'https://www.google.com/maps/?q=' + this.auction.metadata.lat + ',' + this.auction.metadata.lng
      }

    }
  }
</script>

<style lang="scss" scoped>
  @import '../pfx.scss';

  .col12 { @include column(1/2); }

  h2 {
    font-family: 'Encode Sans', sans-serif;
    font-weight: 300;
    font-size: 1.8rem;
  }

  .below-heading {
    @include column(2/3);
    margin-bottom: 1em;
  }
  .some-icons {
    @include column(1/3);
    text-align: right;
  }

  .photo-carousel {
    position: relative;
    margin-bottom: 1rem;

    .mapbox {
      z-index: 1;
      position: absolute;
      right: .5rem;
      top: .5rem;
      //background-color: $color-pfx-brand; // // NOTE: defined the ThemeWrapper: brandColor
      text-align: center;
      color: $color-white;
      padding: 0.5em 1em;
    }
  }

  .property-description {
    padding: 0;
    h4 {
      color: #000;
      font-size: 1.2rem;
    }
  }

  .small {
    font-size: 0.85em;
    word-break: break-all;
  }

  .document {
    a {
      font-weight: 600;
      font-size: 1.2rem;
    }
  }

  .locked-documents {
    text-align: center;
    background-color: #ddd;
    background-image: linear-gradient(#f3f3f3, #ddd);
    padding: 3rem;
  }

</style>
<style lang="scss">

  .custom-tabs-bar-style {
    .tabs__bar {
      background: transparent !important;
    }
    .tabs__items {
      div.card {
        background: transparent !important;
        box-shadow: none;
      }
    }
    .tabs__item {
      text-transform: none;
      font-family: 'Encode Sans', sans-serif;
      color: #888;
    }
    .tabs__item--active {
      color: #000;
      font-weight: 600;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/AuctionPage.vue